import { useLayoutEffect, useMemo, useRef } from "react"
import { useAppSelector } from "hooks/redux"
import { factorsViewSlice, DEFAULT_FACTORS_VIEW_STATE, FACTORS_VIEW_PREFERENCE_BASE, selectFactorsViewPanel } from "connected-components/dashboard/store/factors-view-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyValue = any

interface FactorsViewModuleConfig {
  initialView?: string
  sortMetricId?: string | null
  selectedTableLevels?: string[]
}

/**
 * Panel-scoped factors-view data + actions, read directly from RTK. Pass moduleConfig only from the
 * top-level wrapper; the seed is idempotent, so extra calls from children are safe.
 */
export function useFactorsViewData(moduleConfig?: FactorsViewModuleConfig) {
  const { dashboardId, panelKey, dispatch, modelId, core, trade } = useDashboardSliceData(FACTORS_VIEW_PREFERENCE_BASE)

  const factorsViewState = useAppSelector(
    (s) => selectFactorsViewPanel(s, dashboardId, panelKey) ?? DEFAULT_FACTORS_VIEW_STATE,
  )

  // Layout effect (registered before the sync's usePreferences) so the seed wins even when a
  // cache-warm fallback resolves synchronously on mount and omits the seeded fields.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Wait for config before latching, or a config arriving on a later render never seeds.
    if (moduleConfig == null) return
    seeded.current = true
    dispatch(factorsViewSlice.actions.initFromModuleConfig({
      dashboardId,
      panelId: panelKey,
      view: moduleConfig.initialView,
      sortMetricId: moduleConfig.sortMetricId,
      selectedTableLevels: moduleConfig.selectedTableLevels,
    }))
  }, [dashboardId, panelKey, dispatch, moduleConfig])

  const updaters = useMemo(() => ({
    setView: (view: string) => dispatch(factorsViewSlice.actions.setView({ dashboardId, panelId: panelKey, view })),
    setGroupBy: (groupBy: AnyValue) => dispatch(factorsViewSlice.actions.setGroupBy({ dashboardId, panelId: panelKey, groupBy })),
    setShowNotHeld: (showNotHeld: boolean | null) => dispatch(factorsViewSlice.actions.setShowNotHeld({ dashboardId, panelId: panelKey, showNotHeld })),
    setShowMetricsSelector: (showMetricsSelector: boolean) => dispatch(factorsViewSlice.actions.setShowMetricsSelector({ dashboardId, panelId: panelKey, showMetricsSelector })),
    setSelectedPeriodPerformance: (selectedPeriodPerformance: string) => dispatch(factorsViewSlice.actions.setSelectedPeriodPerformance({ dashboardId, panelId: panelKey, selectedPeriodPerformance })),
    setSortMetricId: (sortMetricId: string | null) => dispatch(factorsViewSlice.actions.setSortMetricId({ dashboardId, panelId: panelKey, sortMetricId })),
    setSortDirection: (sortDirection: "ASC" | "DESC") => dispatch(factorsViewSlice.actions.setSortDirection({ dashboardId, panelId: panelKey, sortDirection })),
    setSelectedTableLevels: (selectedTableLevels: string[]) => dispatch(factorsViewSlice.actions.setSelectedTableLevels({ dashboardId, panelId: panelKey, selectedTableLevels })),
    setDatagridState: (datagridState: AnyValue) => dispatch(factorsViewSlice.actions.setDatagridState({ dashboardId, panelId: panelKey, datagridState })),
  }), [dispatch, dashboardId, panelKey])

  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...factorsViewState,
    ...updaters,
  }), [core, modelId, trade, factorsViewState, updaters])
}
